import type { MicFailure, MicReport } from './types'

/**
 * The pure decisions behind Soundcheck. Every function takes the time it needs
 * as an argument; none of them reads a clock, an observable, or a room.
 */

/** Why a student is not being transcribed, or null when they are. */
export type TranscriptFailure = 'not_listened' | 'no_captions' | null

/**
 * Whether a student whose microphone works is being transcribed.
 *
 * 1. An own caption in the last window means yes.
 * 2. No Secretary heartbeat has listed their mic for a window: the transcriber
 *    is dead or is not listening to this mic.
 * 3. The worker started speech on their mic too many times since their last
 *    caption: it hears them and returns nothing.
 * 4. Otherwise they are quiet, which is not a failure.
 */
export function transcriptFailure(params: {
  now: number
  lastCaptionAt: number | null
  // When a heartbeat last listed the mic, or when the mic became ready if
  // later: the worker needs a moment to list a mic that just appeared.
  listenedSince: number
  speechStartsSinceCaption: number
  windowMS: number
  maxSpeechStartsWithoutCaption: number
}): TranscriptFailure {
  const {
    now,
    lastCaptionAt,
    listenedSince,
    speechStartsSinceCaption,
    windowMS,
    maxSpeechStartsWithoutCaption,
  } = params
  const captionedRecently =
    lastCaptionAt !== null && now - lastCaptionAt < windowMS
  if (captionedRecently) return null
  if (now - listenedSince >= windowMS) return 'not_listened'
  if (speechStartsSinceCaption >= maxSpeechStartsWithoutCaption) {
    return 'no_captions'
  }
  return null
}

/** What a MicCheck report means for a host that tracks its own mic problem. */
export type MicProblemChange =
  | { kind: 'opened'; cause: MicFailure }
  | { kind: 'resolved'; cause: MicFailure }
  | null

/**
 * The microphone problem a MicCheck report opens or resolves, for a host with
 * no SoundcheckController (the AV check). Same rules as the meeting's
 * microphone layer: a failure opens it, a changed cause re-opens it, only
 * ready resolves it, and checking or inactive leaves it as it is.
 */
export function micProblemChange(
  openCause: MicFailure | null,
  report: MicReport
): MicProblemChange {
  if (report.state === 'failed') {
    if (report.cause === openCause) return null
    return { kind: 'opened', cause: report.cause }
  }
  if (report.state === 'ready' && openCause !== null) {
    return { kind: 'resolved', cause: openCause }
  }
  return null
}
